import * as React from "react";
import AllowRoles from "molecules/allow-roles/allow-roles";
import Anchor from "atoms/anchors/anchor";
import Button from "atoms/buttons/button";
import Icon from "atoms/icons/icon";
import useAiChatEnabled from "utilities/hooks/use-ai-chat-enabled";
import useNotebooksEnabled from "utilities/hooks/use-notebooks-enabled";
import { AdminRoleTypes } from "utilities/enumerations/role-type";
import { ButtonSizes } from "atoms/constants/button-sizes";
import { ButtonStyles } from "atoms/constants/button-styles";
import { IconSizes } from "atoms/constants/icon-sizes";
import { Icons } from "atoms/constants/icons";
import { siteMap } from "internal-sitemap";
import { useLocalization } from "utilities/hooks/use-localization";

// -----------------------------------------------------------------------------------------
// #region Interfaces
// -----------------------------------------------------------------------------------------

interface MobileNavigationMenuProps {
    handleLinkClick: (
        e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
    ) => void;
    handleShowSearch: () => void;
    handleShowChat: () => void;
    handleCloseChat: () => void;
    isOpen: boolean;
    setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

// #endregion Interfaces

// -----------------------------------------------------------------------------------------
// #region Constants
// -----------------------------------------------------------------------------------------

const CSS_BASE_CLASSNAME = "c-mobile-navigation-menu";

// #endregion Constants

// -----------------------------------------------------------------------------------------
// #region Component
// -----------------------------------------------------------------------------------------

const MobileNavigationMenu: React.FunctionComponent<
    MobileNavigationMenuProps
> = ({
    handleLinkClick,
    handleShowSearch,
    handleShowChat,
    handleCloseChat,
    isOpen,
    setIsOpen,
}) => {
    const { t } = useLocalization();
    const { useAIChat } = useAiChatEnabled();
    const showChatButton = useAIChat;
    const enableNotebooks = useNotebooksEnabled();

    const handleMenuButtonClick = () => {
        setIsOpen((isOpen) => !isOpen);
    };

    return (
        <div className={CSS_BASE_CLASSNAME}>
            <Button
                accessibleText={t("mobile-nav-menu-button")}
                cssClassName={`${CSS_BASE_CLASSNAME}__button`}
                onClick={handleMenuButtonClick}
                size={ButtonSizes.Medium}
                style={ButtonStyles.None}>
                <Icon
                    size={IconSizes.Large}
                    type={Icons.Menu}
                    cssClassName={`${CSS_BASE_CLASSNAME}__icon`}
                />
            </Button>
            <div
                className={`${CSS_BASE_CLASSNAME}__menu${isOpen ? " -open" : " -close"}`}>
                <div className={`${CSS_BASE_CLASSNAME}__menu-item`}>
                    <Anchor
                        ariaLabel={t("nav-aria-direct-link")}
                        cssClassName={`${CSS_BASE_CLASSNAME}__menu-item-link`}
                        to={siteMap.situationalNavigation.dashboard}
                        onClick={handleLinkClick}>
                        <Icon
                            type={Icons.Sitnav}
                            size={IconSizes.Large}
                            cssClassName={`${CSS_BASE_CLASSNAME}__menu-item-icon`}
                        />
                        <span
                            className={`${CSS_BASE_CLASSNAME}__menu-item-label`}>
                            {t("nfpa-direct")}
                        </span>
                    </Anchor>
                </div>
                <div className={`${CSS_BASE_CLASSNAME}__menu-item`}>
                    <Anchor
                        ariaLabel={t("nav-aria-mylink-link")}
                        cssClassName={`${CSS_BASE_CLASSNAME}__menu-item-link`}
                        to={siteMap.myLink.myBookmarks.dashboard}
                        onClick={handleLinkClick}>
                        <Icon
                            type={Icons.BookmarkOutline}
                            size={IconSizes.Large}
                            cssClassName={`${CSS_BASE_CLASSNAME}__menu-item-icon`}
                        />
                        <span
                            className={`${CSS_BASE_CLASSNAME}__menu-item-label`}>
                            {t("breadcrumbs-myLink-myBookmarks")}
                        </span>
                    </Anchor>
                </div>
                {enableNotebooks && (
                    <div className={`${CSS_BASE_CLASSNAME}__menu-item`}>
                        <Anchor
                            ariaLabel={t("notebook")}
                            cssClassName={`${CSS_BASE_CLASSNAME}__menu-item-link`}
                            to={`${siteMap.notebooks.list}`}
                            onClick={handleLinkClick}>
                            <Icon
                                type={Icons.Notebook}
                                size={IconSizes.Large}
                                cssClassName={`${CSS_BASE_CLASSNAME}__menu-item-icon`}
                            />
                            <span
                                className={`${CSS_BASE_CLASSNAME}__menu-item-label`}>
                                {t("notebooks")}
                            </span>
                        </Anchor>
                    </div>
                )}
                <div className={`${CSS_BASE_CLASSNAME}__menu-item`}>
                    <Button
                        accessibleText={t("nav-aria-search-link")}
                        cssClassName={`${CSS_BASE_CLASSNAME}__menu-item-link`}
                        onClick={handleShowSearch}
                        style={ButtonStyles.Primary}>
                        <Icon
                            type={Icons.Search}
                            size={IconSizes.Large}
                            cssClassName={`${CSS_BASE_CLASSNAME}__menu-item-icon`}
                        />
                        <span
                            className={`${CSS_BASE_CLASSNAME}__menu-item-label`}>
                            {t("search")}
                        </span>
                    </Button>
                </div>
                {showChatButton && (
                    <div className={`${CSS_BASE_CLASSNAME}__menu-item`}>
                        <Button
                            accessibleText={t("nav-aria-chat-link")}
                            cssClassName={`${CSS_BASE_CLASSNAME}__menu-item-link`}
                            onClick={handleShowChat}
                            style={ButtonStyles.Primary}>
                            <Icon
                                type={Icons.Casi}
                                size={IconSizes.Large}
                                cssClassName={`${CSS_BASE_CLASSNAME}__menu-item-icon -casi`}
                            />
                            <span
                                className={`${CSS_BASE_CLASSNAME}__menu-item-label`}>
                                {t("nav-tooltip-chat-description")}
                            </span>
                        </Button>
                    </div>
                )}
                <AllowRoles allowedRoles={AdminRoleTypes}>
                    <div className={`${CSS_BASE_CLASSNAME}__menu-item`}>
                        <Anchor
                            ariaLabel={t("admin-console")}
                            cssClassName={`${CSS_BASE_CLASSNAME}__menu-item-link`}
                            to={siteMap.admin.dashboard}
                            onClick={handleCloseChat}>
                            <Icon
                                type={Icons.Settings}
                                size={IconSizes.Large}
                                cssClassName={`${CSS_BASE_CLASSNAME}__menu-item-icon`}
                            />
                            <span
                                className={`${CSS_BASE_CLASSNAME}__menu-item-label`}>
                                {t("admin-console")}
                            </span>
                        </Anchor>
                    </div>
                </AllowRoles>
            </div>
        </div>
    );
};

// #endregion Component

// -----------------------------------------------------------------------------------------
// #region Exports
// -----------------------------------------------------------------------------------------

export default MobileNavigationMenu;

// #endregion Exports
